import { useMemo, useState } from 'react';
import { Award, Building2, Search, Star, TrendingUp, Users } from 'lucide-react';
import { NORTHWIND_PAST_HIRES, type PastHire } from '@/modules/brain/data/seedCompany';
import { companyApi } from '@/modules/brain/services/company.api';
import { useCompanyResource } from '@/modules/brain/hooks/useCompanyResource';

type TeamFilter = 'all' | PastHire['team'];
type ChannelFilter = 'all' | PastHire['sourceChannel'];

const TEAMS: { id: TeamFilter; label: string }[] = [
  { id: 'all', label: 'All teams' },
  { id: 'Engineering', label: 'Engineering' },
  { id: 'GTM', label: 'GTM' },
  { id: 'Product', label: 'Product' },
  { id: 'Design', label: 'Design' },
  { id: 'G&A', label: 'G&A' },
  { id: 'Operations', label: 'Operations' },
];

const CHANNELS: { id: ChannelFilter; label: string }[] = [
  { id: 'all', label: 'Any source' },
  { id: 'Referral', label: 'Referral' },
  { id: 'Outbound', label: 'Outbound' },
  { id: 'Inbound', label: 'Inbound' },
  { id: 'Network', label: 'Network' },
  { id: 'Boomerang', label: 'Boomerang' },
  { id: 'Agency', label: 'Agency' },
];

function formatHireDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function tenureLabel(months: number) {
  if (months < 12) return `${months}mo`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}y` : `${years}y ${rem}mo`;
}

// Strip the seed-tag prefix used by the seeder for --reseed cleanup.
function cleanNote(note: string | undefined): string {
  if (!note) return '';
  return note.replace(/^\[northwind-seed\]\s*/, '').trim();
}

export function EmploymentHistoryPage() {
  const { value: hires } = useCompanyResource(
    () => companyApi.listHires(),
    NORTHWIND_PAST_HIRES,
    'hires',
  );

  const [team, setTeam] = useState<TeamFilter>('all');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hires.filter((h) => {
      if (team !== 'all' && h.team !== team) return false;
      if (channel !== 'all' && h.sourceChannel !== channel) return false;
      if (!q) return true;
      const haystack = [h.name, h.role, h.previousCompany, ...h.pastCompanies].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [hires, team, channel, query]);

  const stats = useMemo(() => {
    const active = hires.filter((h) => h.status === 'active');
    const topTier = hires.filter((h) => h.perfTier === 'top-10').length;
    const avgRamp =
      hires.length === 0
        ? 0
        : Math.round(hires.reduce((acc, h) => acc + h.rampWeeks, 0) / hires.length);
    const referralCount = hires.filter(
      (h) => h.sourceChannel === 'Referral' || h.sourceChannel === 'Network',
    ).length;
    return {
      total: hires.length,
      active: active.length,
      topTier,
      avgRamp,
      referralPct: Math.round((referralCount / hires.length) * 100),
    };
  }, [hires]);

  const sourcePyramid = useMemo(() => {
    const acc = new Map<string, number>();
    for (const h of hires) {
      acc.set(h.sourceChannel, (acc.get(h.sourceChannel) ?? 0) + 1);
    }
    return [...acc.entries()].sort((a, b) => b[1] - a[1]);
  }, [hires]);

  return (
    <div className="brain-company-page">
      <header className="brain-company-hero brain-company-hero-compact">
        <div className="brain-company-hero-text">
          <p className="brain-company-eyebrow">Brain · Company</p>
          <h1 className="brain-company-title">Employment history</h1>
          <p className="brain-company-tagline">
            {stats.total} hires across the org. Lantern uses this to attribute outreach,
            calibrate the bar, and rank source channels.
          </p>
        </div>
      </header>

      <section className="brain-company-headcount-summary">
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <Users size={12} strokeWidth={1.7} /> Active
          </div>
          <div className="brain-company-summary-tile-value">{stats.active}</div>
          <div className="brain-company-summary-tile-sub">{stats.total - stats.active} departed</div>
        </div>
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <Star size={12} strokeWidth={1.7} /> Top performers
          </div>
          <div className="brain-company-summary-tile-value">{stats.topTier}</div>
          <div className="brain-company-summary-tile-sub">"top 10%" reviews</div>
        </div>
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <TrendingUp size={12} strokeWidth={1.7} /> Avg ramp
          </div>
          <div className="brain-company-summary-tile-value">{stats.avgRamp}<span className="brain-company-summary-tile-unit">wk</span></div>
          <div className="brain-company-summary-tile-sub">to productivity</div>
        </div>
        <div className="brain-company-summary-tile">
          <div className="brain-company-summary-tile-label">
            <Award size={12} strokeWidth={1.7} /> Network sourced
          </div>
          <div className="brain-company-summary-tile-value">{stats.referralPct}<span className="brain-company-summary-tile-unit">%</span></div>
          <div className="brain-company-summary-tile-sub">referrals + network</div>
        </div>
      </section>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Building2 size={14} strokeWidth={1.6} />
          <h2>Where our hires come from</h2>
          <span className="brain-company-section-hint">
            Source channel mix across all hires.
          </span>
        </header>
        <div className="brain-company-source-pyramid">
          {sourcePyramid.map(([source, count]) => {
            const pct = Math.round((count / stats.total) * 100);
            return (
              <div key={source} className="brain-company-source-row">
                <span className="brain-company-source-label">{source}</span>
                <div className="brain-company-source-bar-track">
                  <div
                    className="brain-company-source-bar-fill"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="brain-company-source-count">
                  {count} <span className="brain-company-source-pct">· {pct}%</span>
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Users size={14} strokeWidth={1.6} />
          <h2>Roster</h2>
          <span className="brain-company-section-hint">
            Click filters to slice. Notes mention any calibration that came out of the hire.
          </span>
        </header>

        <div className="brain-company-filter-bar">
          <div className="brain-company-filter-group">
            {TEAMS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`memory-chip${team === t.id ? ' memory-chip-active' : ''}`}
                onClick={() => setTeam(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="brain-company-filter-group">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`memory-chip memory-chip-soft${channel === c.id ? ' memory-chip-active' : ''}`}
                onClick={() => setChannel(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <label className="memory-search">
            <Search size={13} strokeWidth={1.7} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, role, previous company…"
            />
            {query && (
              <button
                type="button"
                className="memory-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </label>
        </div>

        <ul className="brain-company-roster">
          {filtered.map((h) => (
            <li key={h.id} className="brain-company-roster-row">
              <div className="brain-company-roster-avatar">{h.avatarInitials}</div>
              <div className="brain-company-roster-main">
                <div className="brain-company-roster-name-row">
                  <span className="brain-company-roster-name">{h.name}</span>
                  {h.perfTier === 'top-10' && (
                    <span className="brain-company-roster-tag brain-company-roster-tag-top">
                      <Star size={10} strokeWidth={2} /> Top 10%
                    </span>
                  )}
                  {h.status === 'departed' && (
                    <span className="brain-company-roster-tag brain-company-roster-tag-departed">
                      Departed
                    </span>
                  )}
                </div>
                <div className="brain-company-roster-role">
                  {h.role} · {h.team}
                </div>
                {cleanNote(h.notes) && (
                  <p className="brain-company-roster-notes">{cleanNote(h.notes)}</p>
                )}
                <div className="brain-company-roster-history">
                  <Building2 size={11} strokeWidth={1.7} />
                  <span>{h.pastCompanies.join(' → ')}</span>
                </div>
              </div>
              <div className="brain-company-roster-meta">
                <div className="brain-company-roster-meta-row">
                  <span className="brain-company-roster-meta-label">Hired</span>
                  <span>{formatHireDate(h.hiredAt)}</span>
                </div>
                <div className="brain-company-roster-meta-row">
                  <span className="brain-company-roster-meta-label">Tenure</span>
                  <span>{tenureLabel(h.tenureMonths)}</span>
                </div>
                <div className="brain-company-roster-meta-row">
                  <span className="brain-company-roster-meta-label">Source</span>
                  <span>{h.sourceChannel} · {h.sourcedBy}</span>
                </div>
                <div className="brain-company-roster-meta-row">
                  <span className="brain-company-roster-meta-label">Ramp</span>
                  <span>{h.rampWeeks} wks</span>
                </div>
              </div>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="brain-company-roster-empty">
              <p>No hires match these filters.</p>
              <button
                type="button"
                onClick={() => {
                  setTeam('all');
                  setChannel('all');
                  setQuery('');
                }}
              >
                Clear filters
              </button>
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}
